/**
 * App SFX — synthesized WebAudio (Bright Acorns `portal/public/js/sound.js` + world/sheet cues).
 * No audio files. Browsers block AudioContext until a gesture; unlock on first pointer/key.
 */
type ToneOpts = { type?: OscillatorType; gain?: number; when?: number; slide?: number };
type Note = { freq: number; dur: number } & ToneOpts;

let ctx: AudioContext | null = null;
let unlockBound = false;
let audioUnlocked = false;
let keepAlive: { src: AudioScheduledSourceNode; g: GainNode } | null = null;

function getCtx(): AudioContext | null {
  if (!audioUnlocked) return null;
  if (!ctx) {
    const AC = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
  }
  return ctx;
}

function ensureKeepAlive(ac: AudioContext) {
  if (!ac || keepAlive || ac.state !== 'running') return;
  try {
    const g = ac.createGain();
    g.gain.value = 0.00001;
    let src: AudioScheduledSourceNode;
    if (typeof ac.createConstantSource === 'function') {
      src = ac.createConstantSource();
      (src as ConstantSourceNode).offset.value = 0;
    } else {
      src = ac.createOscillator();
      (src as OscillatorNode).frequency.value = 40;
    }
    src.connect(g).connect(ac.destination);
    src.start();
    keepAlive = { src, g };
  } catch { /* ignore */ }
}

export function unlockAudio() {
  try {
    audioUnlocked = true;
    const ac = getCtx();
    if (!ac) return;
    const wasSuspended = ac.state === 'suspended' || (ac.state as string) === 'interrupted';
    if (wasSuspended) void ac.resume();
    if (wasSuspended || ac.state !== 'running') {
      const buf = ac.createBuffer(1, 1, 22050);
      const src = ac.createBufferSource();
      src.buffer = buf;
      src.connect(ac.destination);
      src.start(0);
    }
    const arm = () => ensureKeepAlive(ac);
    if (ac.state === 'running') arm();
    else void Promise.resolve(ac.resume()).then(arm).catch(() => {});
  } catch { /* ignore */ }
}

function bindAudioUnlock() {
  if (unlockBound || typeof window === 'undefined') return;
  unlockBound = true;
  const onGesture = () => unlockAudio();
  for (const ev of ['pointerdown', 'keydown', 'touchstart', 'mousedown'] as const) {
    window.addEventListener(ev, onGesture, { capture: true, passive: true });
  }
}

bindAudioUnlock();

function playTone(ac: AudioContext, freq: number, dur: number, opts: ToneOpts = {}) {
  const { type = 'sine', gain = 0.12, when = 0, slide = 0 } = opts;
  const t0 = ac.currentTime + when;
  const osc = ac.createOscillator();
  const g = ac.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (slide) osc.frequency.exponentialRampToValueAtTime(Math.max(40, freq + slide), t0 + dur);
  g.gain.setValueAtTime(gain, t0);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g).connect(ac.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.05);
}

function playNotes(notes: Note[]) {
  if (!audioUnlocked || !notes.length) return;
  try {
    const ac = getCtx();
    if (!ac) return;
    const run = () => {
      if (ac.state !== 'running') return false;
      for (const n of notes) playTone(ac, n.freq, n.dur, n);
      ensureKeepAlive(ac);
      return true;
    };
    if (run()) return;
    void Promise.resolve(ac.resume())
      .then(() => {
        if (run()) return;
        window.setTimeout(() => { run(); }, 48);
      })
      .catch(() => {});
  } catch { /* stay silent */ }
}

function tone(freq: number, dur: number, opts: ToneOpts = {}) {
  playNotes([{ freq, dur, ...opts }]);
}

export const sfx = {
  grab() {
    tone(520, 0.05, { type: 'triangle', gain: 0.05 });
    tone(720, 0.06, { type: 'sine', gain: 0.04, when: 0.03 });
  },
  petPoke() {
    const base = 880 + Math.floor(Math.random() * 3) * 60;
    tone(base, 0.05, { type: 'square', gain: 0.07 });
    tone(base + 220, 0.07, { type: 'triangle', gain: 0.09, when: 0.04, slide: 80 });
    tone(base - 40, 0.06, { type: 'sine', gain: 0.06, when: 0.09 });
  },
  petFoodPick() {
    tone(700, 0.05, { type: 'triangle', gain: 0.07 });
    tone(920, 0.08, { type: 'sine', gain: 0.08, when: 0.05, slide: 100 });
  },
  petFoodDrop() {
    tone(520, 0.05, { type: 'triangle', gain: 0.06 });
    tone(380, 0.09, { type: 'sine', gain: 0.07, when: 0.04, slide: -120 });
    tone(860, 0.06, { type: 'square', gain: 0.04, when: 0.08 });
  },
  petMunch() {
    tone(220, 0.05, { type: 'triangle', gain: 0.06, slide: -40 });
    tone(300, 0.05, { type: 'triangle', gain: 0.05, when: 0.07, slide: -30 });
    tone(260, 0.06, { type: 'sine', gain: 0.05, when: 0.14, slide: -50 });
    tone(480, 0.04, { type: 'square', gain: 0.03, when: 0.1 });
  },
  petYum() {
    tone(659, 0.07, { type: 'triangle', gain: 0.08 });
    tone(784, 0.08, { type: 'sine', gain: 0.09, when: 0.06 });
    tone(1047, 0.14, { type: 'sine', gain: 0.1, when: 0.13, slide: 60 });
  },
  petAnnoyed() {
    tone(320, 0.1, { type: 'sawtooth', gain: 0.06, slide: -80 });
    tone(240, 0.14, { type: 'triangle', gain: 0.07, when: 0.08, slide: -60 });
    tone(180, 0.12, { type: 'square', gain: 0.04, when: 0.18 });
  },
  petSticker() {
    tone(640, 0.06, { type: 'triangle', gain: 0.07 });
    tone(820, 0.08, { type: 'sine', gain: 0.08, when: 0.05, slide: 120 });
    tone(980, 0.1, { type: 'triangle', gain: 0.06, when: 0.12, slide: -60 });
  },
  petPlay() {
    tone(523, 0.06, { type: 'triangle', gain: 0.08, slide: 80 });
    tone(784, 0.07, { type: 'square', gain: 0.06, when: 0.07 });
    tone(988, 0.08, { type: 'sine', gain: 0.09, when: 0.14, slide: 120 });
    tone(1175, 0.12, { type: 'triangle', gain: 0.07, when: 0.22 });
  },
  /** World-hub pin tap (dolly into a land). Soft paper-pin plink. */
  mapClick() {
    tone(660, 0.045, { type: 'triangle', gain: 0.055 });
    tone(880, 0.07, { type: 'sine', gain: 0.05, when: 0.03, slide: 50 });
  },
  /** Land-map hotspot tap (sheet / activity opens after the double-tap hold-back). */
  landClick() {
    tone(520, 0.05, { type: 'triangle', gain: 0.06 });
    tone(700, 0.08, { type: 'sine', gain: 0.065, when: 0.04, slide: 70 });
    tone(940, 0.05, { type: 'triangle', gain: 0.035, when: 0.1 });
  },
  /** BottomSheet drawer opening — soft rising whoosh matched to the slide-up. */
  sheetOpen() {
    tone(260, 0.14, { type: 'sine', gain: 0.035, slide: 90 });
    tone(390, 0.16, { type: 'triangle', gain: 0.045, when: 0.07, slide: 110 });
    tone(540, 0.18, { type: 'sine', gain: 0.04, when: 0.16, slide: 130 });
    tone(720, 0.2, { type: 'triangle', gain: 0.03, when: 0.28 });
  },
  /** Lunge start — a short falling whiff of air. */
  battleSwing() {
    tone(420, 0.07, { type: 'triangle', gain: 0.045, slide: -180 });
    tone(620, 0.05, { type: 'sine', gain: 0.03, when: 0.02, slide: -220 });
  },
  /** Ordinary impact — thud plus a short mid crack. */
  battleHit() {
    tone(150, 0.09, { type: 'sine', gain: 0.11, slide: -60 });
    tone(320, 0.05, { type: 'square', gain: 0.05, when: 0.01, slide: -120 });
  },
  /** Impact >= 25% of the target's max HP — lower, longer, with a sub thump. */
  battleHitStrong() {
    tone(90, 0.16, { type: 'sine', gain: 0.14, slide: -40 });
    tone(240, 0.09, { type: 'square', gain: 0.07, when: 0.01, slide: -140 });
    tone(60, 0.2, { type: 'sine', gain: 0.09, when: 0.04, slide: -20 });
  },
  /** A PARTY member taking the hit — a rising squeak, species-agnostic. */
  battleHurt() {
    tone(540, 0.08, { type: 'triangle', gain: 0.075, slide: 180 });
    tone(760, 0.07, { type: 'sine', gain: 0.05, when: 0.05, slide: 120 });
  },
  /** Enemy defeated — wet low burst, descending. */
  battleDefeat() {
    tone(200, 0.13, { type: 'sawtooth', gain: 0.09, slide: -110 });
    tone(120, 0.18, { type: 'sine', gain: 0.1, when: 0.06, slide: -60 });
    tone(300, 0.09, { type: 'square', gain: 0.04, when: 0.03, slide: -180 });
  },
  /** A damage:0 attack row. */
  battleMiss() {
    tone(700, 0.06, { type: 'sine', gain: 0.03, slide: -260 });
  },
  /** Level-up (Demo I): a bright C6–E6–G6 arpeggio into a long C7 bell with a quiet inharmonic
   *  partial — one per level crossed; the victory card sequences them so they never overlap. */
  levelUp() {
    tone(1047, 0.12, { type: 'sine', gain: 0.09 });
    tone(1319, 0.12, { type: 'sine', gain: 0.09, when: 0.07 });
    tone(1568, 0.14, { type: 'sine', gain: 0.1, when: 0.14 });
    tone(2093, 0.9, { type: 'sine', gain: 0.12, when: 0.21 });
    tone(5777, 0.45, { type: 'sine', gain: 0.02, when: 0.21 });
  },
};
